import React, { useEffect, useState } from "react";
import CreateReplacementOrderForm from "../../../../../../forms/CreateReplacementOrderForm";
import MarkAsRegretOrderForm from "../../../../../../forms/MarkAsRegretOrderForm";
import { Button, message, Modal, Radio } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import markAsRegretOrderSchema from "../../../../../../schemas/markAsRegretOrderSchema";
import createReplacementOrderSchema from "../../../../../../schemas/createReplacementOrderSchema";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { actions } from "../../slice";
import { reverse } from "named-urls";
import routes from "../../../../../../../routes";

export function MarkRegretOrReplace({
  markAsRegretModalVisible,
  setMarkAsRegretModalVisible,
  commodities = [],
  states = [],
  districts = [],
  samplers = [],
  agencies = [],
  sectors = [],
  markedAsRegret,
  setMarkedAsRegret,
  createdReplacementOrder,
  setCreatedReplacementOrder,
  orderDetails,
  permissions,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const defaultDataMap =
    permissions?.mapPreviousDetails === false ? "createFreshOrder" : "";

  const [createReplacementLoading, setCreateReplacementLoading] =
    useState(false);

  const packages = useSelector((state) => state.orderDetails?.packages || []);

  const projectConfig = orderDetails?.projectCodeId?.projectConfig || {};
  const metadataFields = projectConfig?.metadataFields || {};
  const projectDocuments = projectConfig?.projectDocuments || [];

  const {
    control: createReplacementOrderControl,
    handleSubmit: createReplacementOrderHandleSubmit,
    formState: { errors: createReplacementOrderErrors },
    reset: resetCreateReplacementOrderForm,
    watch: createReplacementOrderWatch,
    setValue: createReplacementOrderSetValue,
  } = useForm({
    resolver: yupResolver(
      createReplacementOrderSchema(metadataFields, projectDocuments),
    ),
    defaultValues: {
      company: orderDetails?.company?._id || "",
      projectCode: orderDetails?.projectCodeId?._id || "",
      agency: "",
      commodity: "",
      sampleVariant: "",
      sampleType: "",
      sector: "",
      state: "",
      district: "",
      destinationCountry: "",
      samplingType: "",
      codewiseReason: "",
      expectedConsignmentReadiness: "",
      expectedShipmentDate: "",
      sampler: "",
      siteAddress: "",
      package: "",
      batchId: "",
      supplier: "",
      quantity: "",
      quantityUnit: "",
      additionalNotes: "",
      replacementReason: "",
      dataMap: defaultDataMap,
      documents: [],
    },
  });

  const {
    control: markAsRegretOrderControl,
    handleSubmit: markAsRegretOrderHandleSubmit,
    formState: { errors: markAsRegretOrderErrors },
    reset: resetMarkAsRegretOrderForm,
    watch: markAsRegretOrderWatch,
    setValue: markAsRegretOrderSetValue,
  } = useForm({
    resolver: yupResolver(markAsRegretOrderSchema),
    defaultValues: {
      rejectionType: "",
      rejectionReason: "",
    },
  });

  const onCreateReplacementOrderSubmit = (data) => {
    setCreateReplacementLoading(true);
    if (markedAsRegret) {
      data = {
        orderId: orderDetails.orderId,
        rejectOnly: "Yes",
        rejectionType: data.rejectionType,
        rejectionReason: data.rejectionReason,
      };
      dispatch(
        actions.createReplacementOrder({
          data,
          onSuccess: () => {
            setMarkAsRegretModalVisible(false);
            setCreateReplacementLoading(false);
            resetMarkAsRegretOrderForm();
            message.success({
              content: "Order marked as regret successfully!",
            });
            dispatch(actions.fetchOrderDetails(orderDetails?._id));
          },
          onFailure: (error) => {
            setCreateReplacementLoading(false);
            message.error({
              content: error?.message || "Failure in marking order as regret",
            });
          },
        }),
      );
    } else {
      const payloadData = {
        orderId: orderDetails.orderId,
        rejectionReason: data.replacementReason,
        rejectionType: "",
        rejectOnly: "No",
        mapDetails: data.dataMap === "mapAvailableData" ? "Yes" : "No",
        stateId: data.state,
        districtId: data.district,
        commodityId: data.commodity,
        sampleVariant: data.sampleVariant,
        sampleType: data.sampleType,
        sector: data.sector,
        projectId: data.projectCode,
        samplerId: data.sampler,
        companyId: data.company,
        agencyId: data.agency,
        destinationCountry: data.destinationCountry,
        samplingType: data.samplingType,
        codewiseReason: data.codewiseReason,
        expectedConsignmentReadiness: data.expectedConsignmentReadiness,
        expectedShipmentDate: data.expectedShipmentDate,
        package: data.package,
        batchId: data.batchId,
        supplier: data.supplier,
        quantity: data.quantity,
        quantityUnit: data.quantityUnit,
        siteAddress: data.siteAddress,
        additionalNotes: data.additionalNotes,
        documents: data.documents,
      };

      dispatch(
        actions.createReplacementOrder({
          data: payloadData,
          onSuccess: (response) => {
            message.success({
              content: "Replacement order created successfully!",
            });
            setMarkAsRegretModalVisible(false);
            setCreateReplacementLoading(false);
            history.push(
              reverse(routes.backOffice.orders.details.view, {
                orderId: response,
              }),
            );
            resetCreateReplacementOrderForm();
          },
          onFailure: (error) => {
            setCreateReplacementLoading(false);
            message.error({
              content:
                error?.message || "Failure in creating replacement order",
            });
          },
        }),
      );
    }
  };

  const selectedState = createReplacementOrderWatch("state");
  const selectedDistrict = createReplacementOrderWatch("district");
  const selectedAgency = createReplacementOrderWatch("agency");

  useEffect(() => {
    if (selectedState) {
      dispatch(actions.fetchDistricts({ stateId: selectedState }));
      createReplacementOrderSetValue("district", "");
    }
  }, [dispatch, selectedState, createReplacementOrderSetValue, markedAsRegret]);

  useEffect(() => {
    if (selectedDistrict && selectedAgency) {
      dispatch(
        actions.fetchSamplers({
          districtId: selectedDistrict,
          stateId: selectedState,
          agencyId: selectedAgency,
        }),
      );
      createReplacementOrderSetValue("sampler", "");
    }
  }, [
    dispatch,
    selectedDistrict,
    selectedState,
    selectedAgency,
    createReplacementOrderSetValue,
  ]);

  const companyId = createReplacementOrderWatch("company");
  const commodityId = createReplacementOrderWatch("commodity");
  const sampleVariant = createReplacementOrderWatch("sampleVariant");
  useEffect(() => {
    if (companyId && commodityId && sampleVariant) {
      dispatch(
        actions.fetchPackages({
          companyId: companyId,
          commodityId: commodityId,
          sampleVariant: sampleVariant,
        }),
      );
    }
  }, [companyId, commodityId, sampleVariant, dispatch]);

  const modalTitle =
    orderDetails?.status?.value === "regret"
      ? "Create Replacement Order"
      : "Mark as Regret";

  return (
    <Modal
      width={950}
      title={modalTitle}
      visible={markAsRegretModalVisible}
      style={{ top: 40 }}
      bodyStyle={{
        maxHeight: "75vh",
        overflowY: "auto",
      }}
      onCancel={() => {
        setMarkAsRegretModalVisible(false);
        resetCreateReplacementOrderForm();
        resetMarkAsRegretOrderForm();
      }}
      footer={[
        <Button
          key="back"
          onClick={() => {
            setMarkAsRegretModalVisible(false);
            resetCreateReplacementOrderForm();
            resetMarkAsRegretOrderForm();
          }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={
            markedAsRegret
              ? markAsRegretOrderHandleSubmit(onCreateReplacementOrderSubmit)
              : createReplacementOrderHandleSubmit(
                  onCreateReplacementOrderSubmit,
                )
          }
          loading={createReplacementLoading}
        >
          Submit
        </Button>,
      ]}
    >
      <div
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "20px",
        }}
      >
        {orderDetails?.status?.value !== "regret" && (
          <Radio.Group
            onChange={(e) => {
              if (e.target.value === "regret") {
                setMarkedAsRegret(true);
                setCreatedReplacementOrder(false);
              } else {
                setMarkedAsRegret(false);
                setCreatedReplacementOrder(true);
              }
            }}
            value={markedAsRegret ? "regret" : "replacement"}
          >
            <Radio value="regret">Mark as Regret Only</Radio>
            <Radio value="replacement">Create Replacement Order</Radio>
          </Radio.Group>
        )}
      </div>
      {createdReplacementOrder && (
        <>
          <CreateReplacementOrderForm
            control={createReplacementOrderControl}
            errors={createReplacementOrderErrors}
            watch={createReplacementOrderWatch}
            setValue={createReplacementOrderSetValue}
            options={{
              commodities: commodities.map((commodity) => ({
                label: commodity.name,
                value: commodity._id,
              })),
              states: states.map((state) => ({
                label: state.name,
                value: state._id,
              })),
              districts: districts.map((district) => ({
                label: district.city,
                value: district._id,
              })),
              samplers: samplers.map((sampler) => ({
                label: sampler.name,
                value: sampler._id,
              })),
              projectCodes: [
                {
                  label: orderDetails?.projectCodeId
                    ? orderDetails.projectCodeId.projectCode
                    : "",
                  value: orderDetails?.projectCodeId
                    ? orderDetails?.projectCodeId._id
                    : "",
                },
              ],
              companies: [
                {
                  label: orderDetails?.company
                    ? orderDetails.company.companyName
                    : "",
                  value: orderDetails?.company ? orderDetails.company._id : "",
                },
              ],
              sectors: sectors,
              agencies: agencies?.map((agency) => ({
                label: agency.agencyName,
                value: agency._id,
              })),
              packages: packages?.map((pkg) => ({
                label: pkg.name,
                value: pkg._id,
              })),
            }}
            permissions={permissions}
            orderDetails={orderDetails}
          />
        </>
      )}

      {orderDetails?.status?.value !== "regret" && markedAsRegret && (
        <MarkAsRegretOrderForm
          control={markAsRegretOrderControl}
          errors={markAsRegretOrderErrors}
          watch={markAsRegretOrderWatch}
        />
      )}
    </Modal>
  );
}
